import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {FILTER_GROUPS} from '../courses.enums';

/** Курсов на странице каталога. */
export const PAGE_SIZE = 6;

/**
 * Состояние каталога — фильтры, поиск и номер страницы — живёт в query-строке URL:
 * /courses?dir=ai-functions&fmt=offline&q=риски&page=2
 *
 * Так сделано намеренно: в корпоративном обучении решение принимает не один человек,
 * и ссылка на отобранную выборку пересылается коллеге как есть. Плюс бесплатно
 * работают «назад» в браузере и обновление страницы.
 */
export const useCourseFilters = (courses, searchSlugs) => {
    const [params, setParams] = useSearchParams();

    const selected = useMemo(() => {
        const out = {};
        FILTER_GROUPS.forEach((g) => {
            out[g.key] = (params.get(g.key) || '').split(',').filter(Boolean);
        });
        return out;
    }, [params]);

    const query = params.get('q') || '';
    const page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);

    const write = useCallback((mutate, {resetPage = true} = {}) => {
        const next = new URLSearchParams(params);
        mutate(next);
        if (resetPage) next.delete('page');
        setParams(next, {replace: true});
    }, [params, setParams]);

    const setQuery = useCallback((value) => {
        write((next) => {
            if (value) next.set('q', value);
            else next.delete('q');
        });
    }, [write]);

    const toggle = useCallback((groupKey, code) => {
        write((next) => {
            const current = (next.get(groupKey) || '').split(',').filter(Boolean);
            const idx = current.indexOf(code);
            if (idx === -1) current.push(code);
            else current.splice(idx, 1);
            if (current.length) next.set(groupKey, current.join(','));
            else next.delete(groupKey);
        });
    }, [write]);

    /** Записать сразу весь набор значений группы — так отдаёт данные Select с mode="multiple". */
    const setGroup = useCallback((groupKey, values) => {
        write((next) => {
            const list = (values || []).filter(Boolean);
            if (list.length) next.set(groupKey, list.join(','));
            else next.delete(groupKey);
        });
    }, [write]);

    const clearGroup = useCallback((groupKey) => {
        write((next) => next.delete(groupKey));
    }, [write]);

    const reset = useCallback(() => {
        setParams(new URLSearchParams(), {replace: true});
    }, [setParams]);

    const setPage = useCallback((value) => {
        write((next) => {
            if (value > 1) next.set('page', String(value));
            else next.delete('page');
        }, {resetPage: false});
    }, [write]);

    const hasActiveFilters = FILTER_GROUPS.some((g) => selected[g.key].length > 0);

    /** Проходит ли курс набор фильтров; skipKey позволяет посчитать «а если снять эту группу». */
    const passes = useCallback((course, skipKey) => {
        if (searchSlugs && !searchSlugs.includes(course.slug)) return false;
        return FILTER_GROUPS.every((g) => {
            if (g.key === skipKey) return true;
            const picked = selected[g.key];
            return picked.length === 0 || picked.includes(course[g.field]);
        });
    }, [selected, searchSlugs]);

    const visible = useMemo(
        () => courses.filter((c) => c.visible !== false),
        [courses],
    );

    const filtered = useMemo(
        () => visible.filter((c) => passes(c)).sort((a, b) => a.sortOrder - b.sortOrder),
        [visible, passes],
    );

    /**
     * Сколько курсов останется, если добавить конкретное значение фильтра.
     * Нулевые значения не прячем, а гасим: пользователь должен видеть границы ассортимента,
     * а не гадать, куда пропал пункт.
     */
    const counts = useMemo(() => {
        const out = {};
        FILTER_GROUPS.forEach((g) => {
            out[g.key] = {};
            const others = visible.filter((c) => passes(c, g.key));
            g.options.forEach((opt) => {
                out[g.key][opt.code] = others.filter((c) => c[g.field] === opt.code).length;
            });
        });
        return out;
    }, [visible, passes]);

    /**
     * Какой одиночный фильтр стоит снять, чтобы выдача перестала быть пустой.
     * Перебор дешёвый: групп три, курсов двенадцать.
     */
    const rescueFilter = useMemo(() => {
        if (filtered.length > 0 || !hasActiveFilters) return null;
        for (let i = 0; i < FILTER_GROUPS.length; i += 1) {
            const g = FILTER_GROUPS[i];
            if (selected[g.key].length === 0) continue;
            const found = visible.filter((c) => passes(c, g.key));
            if (found.length > 0) {
                return {groupKey: g.key, codes: selected[g.key], count: found.length};
            }
        }
        return null;
    }, [filtered.length, hasActiveFilters, selected, visible, passes]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    return {
        selected,
        query,
        setQuery,
        toggle,
        setGroup,
        clearGroup,
        /** Текущее состояние каталога строкой запроса — его запоминает CoursesCatalogSlice. */
        searchString: params.toString(),
        reset,
        hasActiveFilters,
        filtered,
        counts,
        rescueFilter,
        page: safePage,
        setPage,
        totalPages,
        pageItems,
        totalVisible: visible.length,
    };
};

export default useCourseFilters;
